import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Bell,
  Gift,
  Radio,
  RefreshCw,
  Search,
  Sparkles,
  Star,
  ToggleLeft,
  UserPlus,
  Zap,
} from "lucide-react";
import { api } from "../lib/api";

const EVENT_META = {
  "channel.follow": { icon: UserPlus, color: "hsl(var(--currency-paid))" },
  "channel.followed": { icon: UserPlus, color: "hsl(var(--currency-paid))" },
  "channel.subscribe": { icon: Star, color: "hsl(var(--currency-event))" },
  "channel.subscription.message": { icon: Star, color: "hsl(var(--currency-event))" },
  "channel.subscription.gift": { icon: Gift, color: "hsl(var(--currency-event))" },
  "channel.subscription.gifts": { icon: Gift, color: "hsl(var(--currency-event))" },
  "channel.bits.use": { icon: Sparkles, color: "hsl(var(--currency-free))" },
  "channel.cheer": { icon: Sparkles, color: "hsl(var(--currency-free))" },
  "channel.raid": { icon: Zap, color: "hsl(var(--primary))" },
  "channel.channel_points_custom_reward_redemption.add": { icon: Bell, color: "hsl(var(--currency-event))" },
  "stream.online": { icon: Radio, color: "hsl(var(--accent-live))" },
  "stream.offline": { icon: ToggleLeft, color: "hsl(var(--muted-foreground))" },
  "kicks.gifted": { icon: Gift, color: "hsl(var(--currency-paid))" },
};

function baseType(type) {
  return String(type || "").replace(/^twitch\./, "").replace(/^kick\./, "");
}

function eventMeta(type) {
  return EVENT_META[baseType(type)] || { icon: Activity, color: "hsl(var(--muted-foreground))" };
}

function formatDate(value) {
  if (!value) return "No timestamp";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function metadataLine(metadata) {
  if (!metadata || typeof metadata !== "object") return "";
  const bits = metadata.bits || metadata.amount || metadata.gift_amount;
  const tier = metadata.tier || metadata.gift_tier;
  const message = typeof metadata.message === "string" ? metadata.message : "";
  const reward = metadata.reward_title || metadata.reward?.title;
  return [bits ? `Amount: ${bits}` : "", tier ? `Tier: ${tier}` : "", reward ? `Reward: ${reward}` : "", message ? `"${message}"` : ""]
    .filter(Boolean)
    .join(" · ");
}

export default function PlatformEvents() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [source, setSource] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/platform-events", { params: { limit: 120, source } });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load platform events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => {
      const haystack = [
        item.platform,
        item.type,
        item.label,
        item.detail,
        item.processing_status,
        item.processing_error,
        metadataLine(item.metadata),
      ].join(" ").toLowerCase();
      return haystack.includes(needle);
    });
  }, [items, query]);

  return (
    <div className="space-y-5" data-testid="platform-events-page">
      <section className="surface p-5 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Twitch and Kick</div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Activity className="w-6 h-6 text-[hsl(var(--primary))]" /> Platform Events
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-3xl">
            Follows, subs, raids, cheers, channel point redeems, Kick events, and stream state changes without chat messages.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <Stat label="Loaded" value={counts.total || 0} />
          <Stat label="Twitch" value={counts.twitch || 0} />
          <Stat label="Kick" value={counts.kick || 0} />
          <Stat label="Failed" value={counts.failed || 0} danger={Number(counts.failed || 0) > 0} />
        </div>
      </section>

      <section className="surface p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="inline-flex rounded border border-[hsl(var(--border))] overflow-hidden w-fit">
          {["all", "twitch", "kick"].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setSource(option)}
              className={`h-10 px-4 text-xs uppercase tracking-widest ${source === option ? "bg-[hsl(var(--surface-2))] text-white" : "text-[hsl(var(--muted-foreground))] hover:text-white"}`}
            >
              {option}
            </button>
          ))}
        </div>
        <label className="h-10 flex-1 min-w-0 flex items-center gap-2 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
          <Search className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search events"
            className="w-full bg-transparent outline-none text-sm"
          />
        </label>
        <button
          type="button"
          onClick={load}
          className="h-10 px-4 inline-flex items-center justify-center gap-2 rounded border border-[hsl(var(--border))] text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] hover:text-white"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </section>

      {error && (
        <div className="rounded border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/10 p-3 text-sm text-[hsl(var(--destructive))] flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      <section className="surface overflow-hidden">
        {loading && <div className="p-8 text-sm text-[hsl(var(--muted-foreground))]">Loading platform events...</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-8 text-sm text-[hsl(var(--muted-foreground))]">No platform events found.</div>
        )}
        <div className="divide-y divide-[hsl(var(--border))]">
          {filtered.map((item) => (
            <PlatformEventRow key={`${item.platform}-${item.id}-${item.type}`} item={item} />
          ))}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, danger = false }) {
  return (
    <div className={`rounded border px-3 py-2 ${danger ? "border-[hsl(var(--destructive))]/50 text-[hsl(var(--destructive))]" : "border-[hsl(var(--border))]"}`}>
      <div className="label-eyebrow">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function PlatformEventRow({ item }) {
  const meta = eventMeta(item.type);
  const Icon = meta.icon;
  const extra = metadataLine(item.metadata);
  return (
    <article className="p-4 flex gap-4 items-start">
      <div className="w-10 h-10 rounded flex items-center justify-center shrink-0" style={{ background: `${meta.color}1A`, color: meta.color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-xs uppercase tracking-widest" style={{ color: meta.color }}>{item.platform}</span>
          <h2 className="text-base font-semibold">{item.label}</h2>
          <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{formatDate(item.at)}</span>
          {item.processing_status && (
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${item.processing_status === "failed" ? "border-[hsl(var(--destructive))]/50 text-[hsl(var(--destructive))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
              {item.processing_status}
            </span>
          )}
        </div>
        {item.detail && <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))] break-words">{item.detail}</div>}
        {extra && <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))] break-words">{extra}</div>}
        {item.processing_error && <div className="mt-2 text-xs text-[hsl(var(--destructive))] break-words">{item.processing_error}</div>}
        <div className="mt-2 font-mono text-[11px] text-[hsl(var(--muted-foreground))] break-all">{item.type}</div>
      </div>
    </article>
  );
}
